import * as THREE from 'three'
import { ITEMS, ItemId } from '../items'
import Bag from '../ui/bag'

type Drop = {
  itemId: ItemId
  count: number
  position: THREE.Vector3
  element: HTMLDivElement
  spawnedAt: number
}

const PICKUP_DELAY = 500
const PICKUP_RADIUS = 2.2
const DESPAWN_TIME = 5 * 60 * 1000
const MAX_DROPS = 40

/**
 * Item drops lying in the world, picked up when the player comes close
 */
export default class Drops {
  constructor(camera: THREE.PerspectiveCamera, bag: Bag) {
    this.camera = camera
    this.bag = bag
  }

  camera: THREE.PerspectiveCamera
  bag: Bag
  drops: Drop[] = []
  vector = new THREE.Vector3()

  spawn = (itemId: ItemId, position: THREE.Vector3, count = 1) => {
    if (this.drops.length >= MAX_DROPS) {
      return
    }
    const item = ITEMS[itemId]
    const element = document.createElement('div')
    element.className = 'world-drop'
    element.innerHTML = `<img src="${item.icon}" alt="${item.name}" />`
    document.body.appendChild(element)
    this.drops.push({
      itemId,
      count,
      position: position.clone(),
      element,
      spawnedAt: performance.now()
    })
  }

  clear = () => {
    for (const drop of this.drops) {
      drop.element.remove()
    }
    this.drops = []
  }

  // bag must fit the whole stack, otherwise the drop stays
  canAccept = (itemId: ItemId, count: number) => {
    let space = 0
    for (const slot of this.bag.slots) {
      if (!slot) {
        space += 64
      } else if (slot.itemId === itemId) {
        space += 64 - slot.count
      }
    }
    return space >= count
  }

  update = () => {
    const now = performance.now()
    for (let i = this.drops.length - 1; i >= 0; i--) {
      const drop = this.drops[i]

      if (now - drop.spawnedAt > DESPAWN_TIME) {
        drop.element.remove()
        this.drops.splice(i, 1)
        continue
      }

      // pickup when the player gets close
      if (
        now - drop.spawnedAt > PICKUP_DELAY &&
        Math.abs(drop.position.y - this.camera.position.y) < 3 &&
        drop.position.distanceTo(this.camera.position) < PICKUP_RADIUS &&
        this.canAccept(drop.itemId, drop.count)
      ) {
        this.bag.addItem(drop.itemId, drop.count)
        this.bag.showDrop(drop.itemId, drop.count)
        drop.element.remove()
        this.drops.splice(i, 1)
        continue
      }

      // bob animation + screen projection
      this.vector.copy(drop.position)
      this.vector.y += 0.4 + Math.sin((now - drop.spawnedAt) / 320) * 0.12
      this.vector.project(this.camera)
      if (this.vector.z < -1 || this.vector.z > 1) {
        drop.element.style.display = 'none'
        continue
      }
      drop.element.style.display = ''
      drop.element.style.left = `${
        (this.vector.x * 0.5 + 0.5) * window.innerWidth
      }px`
      drop.element.style.top = `${
        (-this.vector.y * 0.5 + 0.5) * window.innerHeight
      }px`
    }
  }
}
